// 介錯の致命「執行」の4枚 (overhead_raise / downswing_smear / exec_contact / overhead_recover) の横の置き方。
//
// 2026-10-09 夜に比較版 → 同日ユーザー採用 (exec.pick「中間」＋ exec.contactArt「使う」) で、配布版の既定 = mid (足と頭の中間に置く・打点は新しい絵 exec_contact_v2)。
//   旧 (bbox の中心を boss.x に置く・打点は exec_contact) は DEV の ?execFinisherFoot=old。=foot / =1 も比較用に残す。
//   本番化の記録 = docs/research/2026-10-09-prod-switch-brace-finisher-counter.md
// 作りは jab (EXEC_JAB_LUNGE_ANCHOR_X) と発勁 (FAJIN_DOWNCUT_ART.footX) と同じ「足に合わせる」:
//   絵の不透明部分の外接矩形の中心ではなく、体重の乗る後ろ足の靴の中心 x を、待機の後ろ足と同じ位置 (boss.x の背中側) に置く。
//   刃やマントの広がりが絵ごとに違っても、体が横へ跳ばない。
// 点検 = docs/research/2026-10-09-executioner-finisher-frames-inspect.md §4 / 結果 = docs/research/2026-10-09-executioner-finisher-foot-anchor.md
//
// rearBootX = 絵の画素での後ろ足の靴の中心 x (測定スクリプト docs/research/2026-10-09-executioner-finisher-frames-inspect.measure.py の
//   靴の窓の中の最下端の平均。誤差は絵の上で ±10px 前後 = 画面で ±1.5px)。
// IDLE_REAR_FROM_CENTER_PX = 待機の絵で、後ろ足の靴が boss.x (bbox の中心) から背中側へ何 px の所にあるか (画面 px・同じ測定)。
//   致命の4枚の後ろ足を、この位置へ揃える。
export type FinisherFootVariant = 'blade' | 'cannon'

export const EXEC_FINISHER_REAR_BOOT_X: Record<FinisherFootVariant, Record<string, number>> = {
  blade: { overhead_raise: 657, downswing_smear: 1003, exec_contact: 1134, overhead_recover: 833,
    // 2026-10-20 据え斬り (sue_*): 後ろ足の靴の中心 x (靴底から45px以内の列の塊の中心・前の4枚もこの測り方で ±4px)
    sue_raise: 730, sue_contact: 969, sue_flick: 629,
    // 2026-10-21 致命を弾かれた絵 (exec_parried_1021・src/exec_parry_recoil.ts): 後ろ足 (右) の靴の塊 1051〜1175 の中心
    exec_parried_1021: 1113 },
  cannon: { overhead_raise: 738, downswing_smear: 1133, exec_contact: 1268, overhead_recover: 914 },
}

export const IDLE_REAR_FROM_CENTER_PX: Record<FinisherFootVariant, number> = { blade: 39.0, cannon: 48.4 }

// 刃の打点の新しい絵 (前足のブーツが見える exec_contact_v2・1356x1160) の後ろ足の靴の中心 x (絵の画素)。
export const EXEC_CONTACT_V2_REAR_BOOT_X = 1296

// 第3案 mid: 横の基準を「後ろ足の靴」と「頭」の中間 (= 腰の代わり。_marks.json に骨盤の印は無いので頭と後ろ足の中点) に置く。
//   後ろ足の滑りと頭の跳びを半分ずつに分ける。headX = _marks.json の head[0] (絵の画素・頭頂の x)。
export const MID_HEAD_WEIGHT = 0.5
export const EXEC_FINISHER_HEAD_X: Record<FinisherFootVariant, Record<string, number>> = {
  blade: { overhead_raise: 368, downswing_smear: 678, exec_contact: 457, overhead_recover: 509,
    // 2026-10-20 据え斬り (sue_*): 髪の画素の重心 x (overhead_raise はこの測り方で 362 = ±6px)
    sue_raise: 411, sue_contact: 190, sue_flick: 279,
    // 2026-10-21 弾かれの絵: 頭の中心 (外套の明るい布を拾わないよう目読みで補正)
    exec_parried_1021: 350 },
  cannon: { overhead_raise: 430, downswing_smear: 749, exec_contact: 501, overhead_recover: 562 },
}
export const EXEC_CONTACT_V2_HEAD_X = 526
// 待機の頭頂が、待機の bbox 中心から背中側へ何 px か (画面 px・(待機の頭頂 x − bbox 中心) × 倍率)。
export const IDLE_HEAD_FROM_CENTER_PX: Record<FinisherFootVariant, number> = { blade: -1.43, cannon: 6.66 }

// 比較版の時の「致命の当たりの前端」(boss.x から前方へ・px)。比較版の打点の絵の最前端 (刃先) を測った値 (測定スクリプト・688〜850ms の最大)。
//   「当たりは絵に合わせる」(src/exec_awoken_reach.ts) の絵の前端の値。刃と砲で値が違うので大きい方 (刃) を採る。
//   旧 (?execFinisherFoot=old) は null = exec_awoken_reach.ts の表の 100 のまま。既定 (mid) は 116。
export const FINISHER_REACH_FRONT_PX = { foot: 148, one: 149, mid: 116 } as const

type FootMode = 'off' | 'foot' | 'one' | 'mid'
// 既定 = mid (10-09 ユーザー採用・配布版)。
export const FINISHER_FOOT_DEFAULT_MODE: FootMode = 'mid'
let footMode: FootMode = FINISHER_FOOT_DEFAULT_MODE
/** DEV の URL の値を受け取る (game.ts の DEV ブロックが呼ぶ)。?execFinisherFoot=old = 旧 (bbox の中心・旧の打点の絵) /
 *  =foot = 足合わせだけ / =1 = 足合わせ + 刃の打点を新しい絵 (exec_contact_v2) へ / =mid = 足と頭の中間 + 新しい絵。
 *  それ以外 (指定なし) は既定の mid。 */
export function setFinisherFootAnchor(value: string | null): void {
  footMode = value === 'old' ? 'off' : value === 'foot' ? 'foot' : value === '1' ? 'one' : value === 'mid' ? 'mid' : FINISHER_FOOT_DEFAULT_MODE
}
export function finisherFootAnchorOn(): boolean {
  return footMode !== 'off'
}
/** 刃の打点を exec_contact_v2 へ差し替えるか (既定 mid は true。配布版は sprites.ts の SPRITE_FILE_OVERRIDE の初期値が持ち、
 *  DEV で旧・foot へ切り替えた時だけ game.ts の DEV ブロックが差し替えを外す)。 */
export function finisherContactV2On(): boolean {
  return footMode === 'one' || footMode === 'mid'
}
/** 致命の当たりの前端 (px)。旧 (?execFinisherFoot=old) は null = exec_awoken_reach.ts の表の値。既定 (mid) は 116。 */
export function finisherReachFrontPx(): number | null {
  return footMode === 'off' ? null : FINISHER_REACH_FRONT_PX[footMode]
}

/** 足合わせ (foot|1|mid・既定 mid) が効いていて、この絵が致命の4枚なら、置く点 (絵の画素 x) と、その点が待機の絵で bbox 中心から背中側へ何 px の所にあるか (画面px) を返す。 */
export function finisherFootAnchorFor(
  bossId: string, variant: string | null, key: string,
): { footX: number; fromCenterPx: number } | undefined {
  if (footMode === 'off' || bossId !== 'the-executioner') return undefined
  if (variant !== 'blade' && variant !== 'cannon') return undefined
  const v2 = variant === 'blade' && key === 'exec_contact' && finisherContactV2On()
  const rear = v2 ? EXEC_CONTACT_V2_REAR_BOOT_X : EXEC_FINISHER_REAR_BOOT_X[variant][key]
  if (rear === undefined) return undefined
  if (footMode !== 'mid') return { footX: rear, fromCenterPx: IDLE_REAR_FROM_CENTER_PX[variant] }
  const head = v2 ? EXEC_CONTACT_V2_HEAD_X : EXEC_FINISHER_HEAD_X[variant][key]!
  const w = MID_HEAD_WEIGHT
  return {
    footX: rear + w * (head - rear),
    fromCenterPx: IDLE_REAR_FROM_CENTER_PX[variant] + w * (IDLE_HEAD_FROM_CENTER_PX[variant] - IDLE_REAR_FROM_CENTER_PX[variant]),
  }
}
